import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, UserPlus, ShieldCheck, Zap, Mail, Copy, RefreshCw, Trash2, Eye,
  CheckCircle2, Clock, Award, Loader2, UploadCloud, XCircle, KeyRound, Search,
} from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

async function copyText(text, label = "Copied to clipboard") {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(label);
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success(label);
    } catch {
      toast.message("Copy manually", { description: text });
    }
  }
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
        <Icon className={`h-4 w-4 ${accent}`} />
      </div>
      <div className="mt-3 font-display font-bold text-3xl tracking-tight">{value}</div>
    </div>
  );
}

function relTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch { return "—"; }
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  // provision dialog
  const [provOpen, setProvOpen] = useState(false);
  const [prov, setProv] = useState({ name: "", email: "", role: "student" });
  const [provBusy, setProvBusy] = useState(false);
  const [newCreds, setNewCreds] = useState(null); // {name, email, temp_password, email_sent, email_enabled}

  const [detail, setDetail] = useState(null);
  const [roleDraft, setRoleDraft] = useState("student");
  const [toDelete, setToDelete] = useState(null);

  // bulk provisioning state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const load = () => {
    api.get("/admin/students").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const provision = async (e) => {
    e.preventDefault();
    setProvBusy(true);
    try {
      const { data: res } = await api.post("/admin/users", prov);
      setNewCreds({ name: prov.name, email: prov.email, temp_password: res.temp_password, email_sent: res.email_sent, email_enabled: res.email_enabled });
      setProv({ name: "", email: "", role: "student" });
      load();
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setProvBusy(false);
    }
  };

  const reinvite = async (s) => {
    try {
      const { data: res } = await api.post(`/admin/users/${s.id}/reinvite`);
      setNewCreds({ name: s.name, email: s.email, temp_password: res.temp_password, email_sent: res.email_sent, email_enabled: res.email_enabled });
      setProvOpen(true);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  // ---- bulk provisioning ----
  const parseBulk = (text) => {
    const rows = [];
    for (const raw of text.split("\n")) {
      const line = raw.trim();
      if (!line) continue;
      const parts = line.split(",").map((p) => p.trim());
      if (parts[0]?.toLowerCase() === "name" && parts[1]?.toLowerCase() === "email") continue; // header
      const [name, email, role] = parts;
      if (!name || !email) continue;
      const r = role === "admin" ? "admin" : role === "instructor" ? "instructor" : "student";
      rows.push({ name, email, role: r });
    }
    return rows;
  };

  const bulkSubmit = async () => {
    const users = parseBulk(bulkText);
    if (users.length === 0) {
      toast.error("Add at least one line: Name, email@example.com, role");
      return;
    }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/users/bulk", { users });
      setBulkResult(data);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBulkBusy(false);
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/admin/users/${toDelete.id}`);
      toast.success("User removed");
      setToDelete(null);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openDetail = (s) => { setRoleDraft(s.role); setDetail(s); };

  const changeRoleInline = async (s, role) => {
    if (role === s.role) return;
    try {
      await api.patch(`/admin/users/${s.id}/role`, { role });
      toast.success(`${s.name} is now ${role}`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const changeRole = async () => {
    if (!detail || roleDraft === detail.role) { setDetail(null); return; }
    try {
      await api.patch(`/admin/users/${detail.id}/role`, { role: roleDraft });
      toast.success(`${detail.name} is now ${roleDraft}`);
      setDetail(null);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (loading) return <div className="p-10 text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading admin portal…</div>;

  const stats = data?.stats || {};
  const students = data?.students || [];
  const q = query.trim().toLowerCase();
  const visibleStudents = q
    ? students.filter((s) =>
        [s.name, s.email, s.role, s.status].some((v) => (v || "").toLowerCase().includes(q)))
    : students;

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-6 w-6 text-primary" />
            <h1 className="font-display font-bold text-3xl tracking-tight">Admin Portal</h1>
          </div>
          <p className="text-muted-foreground mt-1">Provision learners, send invites, and monitor cohort progress.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="gap-2" onClick={() => navigate("/admin/achievements")} data-testid="program-achievements-btn">
            <Award className="h-4 w-4" /> Program & Badges
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => navigate("/admin/temp-passwords")} data-testid="view-temp-passwords-btn">
            <KeyRound className="h-4 w-4" /> View Temporary Passwords
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => { setBulkResult(null); setBulkText(""); setBulkOpen(true); }} data-testid="bulk-add-btn">
            <UploadCloud className="h-4 w-4" /> Bulk add
          </Button>
          <Button className="gap-2" onClick={() => { setNewCreds(null); setProvOpen(true); }} data-testid="provision-user-btn">
            <UserPlus className="h-4 w-4" /> Provision user
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Stat icon={Users} label="Learners" value={stats.total || 0} accent="text-primary" />
        <Stat icon={CheckCircle2} label="Active" value={stats.active || 0} accent="text-success" />
        <Stat icon={Mail} label="Pending Invites" value={stats.invited || 0} accent="text-amber-500" />
        <Stat icon={Zap} label="Avg XP" value={stats.avg_xp || 0} accent="text-primary" />
      </div>

      <div className="rounded-lg border border-border bg-card mt-8 overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-4 flex-wrap">
          <h2 className="font-display font-bold text-lg">Learners</h2>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search by name, email, role…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="admin-user-search"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">XP</TableHead>
                <TableHead>Labs</TableHead>
                <TableHead className="text-center">Streak</TableHead>
                <TableHead className="text-center">Certs</TableHead>
                <TableHead>Last active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleStudents.map((s) => (
                <TableRow key={s.id} data-testid={`student-row-${s.id}`}>
                  <TableCell>
                    <div className="font-medium">{s.name}</div>
                    <div className="text-xs text-muted-foreground">{s.email}</div>
                  </TableCell>
                  <TableCell>
                    <Select value={s.role} onValueChange={(v) => changeRoleInline(s, v)}>
                      <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`role-select-${s.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="student">Student</SelectItem>
                        <SelectItem value="instructor">Instructor</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    {s.status === "invited" ? (
                      <Badge variant="outline" className="text-amber-600 border-amber-500/40 gap-1"><Clock className="h-3 w-3" /> Invited</Badge>
                    ) : (
                      <Badge variant="secondary" className="gap-1"><CheckCircle2 className="h-3 w-3 text-success" /> Active</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono">{s.xp}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 min-w-[120px]">
                      <Progress value={s.completion_pct} className="h-1.5 w-16" />
                      <span className="text-xs text-muted-foreground font-mono">{s.completed_count}/{s.total_labs}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">{s.streak > 0 ? `${s.streak}🔥` : "—"}</TableCell>
                  <TableCell className="text-center">{s.certificates_earned}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{relTime(s.last_active)}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Reset password & resend credentials" onClick={() => reinvite(s)} data-testid={`reinvite-${s.id}`}>
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="View & manage" onClick={() => openDetail(s)} data-testid={`view-student-${s.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" title="Remove user" onClick={() => setToDelete(s)} data-testid={`delete-student-${s.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {visibleStudents.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">{query ? `No learners match "${query}".` : "No learners yet. Provision your first user."}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Provision dialog */}
      <Dialog open={provOpen} onOpenChange={(o) => { setProvOpen(o); if (!o) setNewCreds(null); }}>
        <DialogContent data-testid="provision-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Provision a new user</DialogTitle>
            <DialogDescription>Create an account and automatically email the login credentials to the user.</DialogDescription>
          </DialogHeader>

          {!newCreds ? (
            <form onSubmit={provision} className="space-y-4" data-testid="provision-form">
              <div className="space-y-1.5">
                <Label htmlFor="pname">Full name</Label>
                <Input id="pname" data-testid="prov-name" value={prov.name} onChange={(e) => setProv((p) => ({ ...p, name: e.target.value }))} required placeholder="Jordan Lee" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pemail">Email</Label>
                <Input id="pemail" type="email" data-testid="prov-email" value={prov.email} onChange={(e) => setProv((p) => ({ ...p, email: e.target.value }))} required placeholder="jordan@company.com" />
              </div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={prov.role} onValueChange={(v) => setProv((p) => ({ ...p, role: v }))}>
                  <SelectTrigger data-testid="prov-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">Student</SelectItem>
                    <SelectItem value="instructor">Instructor</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={provBusy} data-testid="prov-submit">{provBusy ? "Creating…" : "Create & send invite"}</Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4" data-testid="creds-created">
              <div className="flex items-center gap-2 text-success text-sm"><CheckCircle2 className="h-4 w-4" /> {newCreds.name}'s account is ready</div>

              {newCreds.email_sent ? (
                <div className="flex items-start gap-2 rounded-md border border-success/30 bg-success/5 p-3 text-sm">
                  <Mail className="h-4 w-4 text-success mt-0.5 shrink-0" />
                  <span>An invite email with login credentials was sent to <b>{newCreds.email}</b>. They'll set a new password on first sign-in.</span>
                </div>
              ) : (
                <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
                  <Mail className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <span>{newCreds.email_enabled ? "The email could not be sent (check the sender domain verification)." : "Email sending is not configured yet."} Share these credentials with the user manually — they'll change the password on first sign-in.</span>
                </div>
              )}

              <div className="rounded-md border border-border p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm"><span className="text-muted-foreground">Username: </span><span className="font-mono">{newCreds.email}</span></div>
                  <Button size="sm" variant="ghost" className="h-7 gap-1" onClick={() => copyText(newCreds.email, "Email copied")}><Copy className="h-3.5 w-3.5" /></Button>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm"><span className="text-muted-foreground">Temp password: </span><span className="font-mono" data-testid="temp-password-value">{newCreds.temp_password}</span></div>
                  <Button size="sm" variant="ghost" className="h-7 gap-1" onClick={() => copyText(newCreds.temp_password, "Password copied")} data-testid="copy-temp-password"><Copy className="h-3.5 w-3.5" /></Button>
                </div>
                <Button size="sm" variant="outline" className="w-full gap-1.5" data-testid="copy-both-creds"
                  onClick={() => copyText(`HMG Linux Lab login\nURL: ${window.location.origin}/login\nUsername: ${newCreds.email}\nTemporary password: ${newCreds.temp_password}`, "Login details copied")}>
                  <Copy className="h-3.5 w-3.5" /> Copy all login details
                </Button>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setNewCreds(null)}>Provision another</Button>
                <Button onClick={() => { setProvOpen(false); setNewCreds(null); }}>Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk add dialog */}
      <Dialog open={bulkOpen} onOpenChange={(o) => { setBulkOpen(o); if (!o) setBulkResult(null); }}>
        <DialogContent data-testid="bulk-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Bulk add users</DialogTitle>
            <DialogDescription>One user per line: <span className="font-mono">Name, email@example.com, role</span>. Role is optional (defaults to student). Each user is emailed their login credentials.</DialogDescription>
          </DialogHeader>

          {!bulkResult ? (
            <div className="space-y-4">
              <Textarea
                data-testid="bulk-textarea"
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                rows={8}
                className="font-mono text-sm"
                placeholder={"Ada Lovelace, ada@company.com, student\nLinus Torvalds, linus@company.com, instructor\nGrace Hopper, grace@company.com"}
              />
              <div className="text-xs text-muted-foreground">Up to 200 users per batch. Duplicate or existing emails are skipped and reported.</div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
                <Button onClick={bulkSubmit} disabled={bulkBusy} data-testid="bulk-submit">{bulkBusy ? "Creating…" : "Create & send invites"}</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4" data-testid="bulk-result">
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="flex items-center gap-1.5 text-success"><CheckCircle2 className="h-4 w-4" /> {bulkResult.created} created</span>
                {bulkResult.failed > 0 && <span className="flex items-center gap-1.5 text-amber-600"><XCircle className="h-4 w-4" /> {bulkResult.failed} skipped</span>}
                <span className="flex items-center gap-1.5 text-muted-foreground"><Mail className="h-4 w-4" /> {bulkResult.emails_sent} emailed</span>
              </div>
              {!bulkResult.email_enabled && bulkResult.created > 0 && (
                <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm text-muted-foreground">
                  Email isn't configured yet, so share these temporary passwords manually. Each user must change it on first sign-in.
                </div>
              )}
              <div className="max-h-72 overflow-y-auto rounded-md border border-border divide-y divide-border">
                {bulkResult.results.map((r, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`bulk-row-${i}`}>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{r.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{r.email}</div>
                    </div>
                    {r.ok ? (
                      <div className="flex items-center gap-2 shrink-0">
                        {!bulkResult.email_enabled && <code className="font-mono text-xs bg-secondary px-2 py-1 rounded">{r.temp_password}</code>}
                        {r.email_sent
                          ? <span className="text-xs text-success flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> sent</span>
                          : <span className="text-xs text-success flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> created</span>}
                      </div>
                    ) : (
                      <span className="text-xs text-amber-600 shrink-0">{r.error}</span>
                    )}
                  </div>
                ))}
              </div>
              <DialogFooter>
                {!bulkResult.email_enabled && bulkResult.created > 0 && (
                  <Button variant="outline" onClick={() => copyText(
                    bulkResult.results.filter((r) => r.ok).map((r) => `${r.name}, ${r.email}, ${r.temp_password}`).join("\n"),
                    "All credentials copied"
                  )} data-testid="bulk-copy-all">Copy all credentials</Button>
                )}
                <Button onClick={() => { setBulkOpen(false); setBulkResult(null); setBulkText(""); }}>Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Student detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="student-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{detail?.name}</DialogTitle>
            <DialogDescription>{detail?.email} · {detail?.role}</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3 text-center">
                <div className="rounded-md border border-border p-3"><div className="font-display font-bold text-xl">{detail.xp}</div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">XP</div></div>
                <div className="rounded-md border border-border p-3"><div className="font-display font-bold text-xl">L{detail.level}</div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Level</div></div>
                <div className="rounded-md border border-border p-3"><div className="font-display font-bold text-xl">{detail.streak}</div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Streak</div></div>
                <div className="rounded-md border border-border p-3 flex flex-col items-center"><div className="font-display font-bold text-xl flex items-center gap-1"><Award className="h-4 w-4 text-primary" />{detail.certificates_earned}</div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Certs</div></div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Per-level progress</div>
                <div className="space-y-2.5">
                  {detail.level_progress.map((lp) => (
                    <div key={lp.id}>
                      <div className="flex justify-between text-sm mb-1"><span>L{lp.id} · {lp.title}</span><span className="font-mono text-muted-foreground">{lp.done}/{lp.total}</span></div>
                      <Progress value={lp.total ? (100 * lp.done) / lp.total : 0} className="h-1.5" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-md border border-border p-3">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Role & access</div>
                <div className="flex items-center gap-2">
                  <Select value={roleDraft} onValueChange={setRoleDraft}>
                    <SelectTrigger data-testid="detail-role-select" className="flex-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="student">Student</SelectItem>
                      <SelectItem value="instructor">Instructor</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={changeRole} disabled={roleDraft === detail.role} data-testid="save-role-btn">Save role</Button>
                </div>
                {roleDraft === "admin" && detail.role !== "admin" && (
                  <p className="text-xs text-amber-600 mt-2">Admins get full access to this portal — provisioning, all learner data, and role management.</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently deletes the account, their progress, and any pending invites. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-user">Remove user</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
